import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IAutomationActionTemplateMicrosoftAddToGroup,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import { isEqual } from 'lodash';
import { SetStateAction, useMemo } from 'react';

import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../../components/SomethingWentWrong';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { useCurrentUser } from '../../../../../../../services/currentUser';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { toSearchTermRegexp } from '../../../../../../../services/user';
import { useGroupList } from '../../../../../../Org/pages/Integrations/integrations/microsoft/groups';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  VALIDATORS_MICROSOFT_ADD_TO_GROUP,
  useFieldValidator
} from '../../validation';

type MicrosoftGroupDescription = {
  id: string;
  name: string;
};

const GroupSelector = ({
  error,
  helperText,
  onBlur,
  onChange,
  value,
  groups
}: {
  error?: boolean;
  helperText?: string | null;
  onChange: (nextValue: MicrosoftGroupDescription) => void;
  onBlur: () => void;
  value: {
    id: string;
    name: string;
  };
  groups: MicrosoftGroupDescription[];
}) => {
  const { t } = useUnsafeTranslation();

  const sortedOptions = groups.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <AutocompleteSingle
      fullWidth={true}
      value={value}
      label={t('Which group should the member be added to')}
      options={sortedOptions}
      getOptionLabel={(o) => o.name}
      getOptionSelected={(o, v) => o.id === v.id}
      helperText={helperText}
      error={!!error}
      required
      onChange={(groupNext) => groupNext && onChange(groupNext)}
      onBlur={() => onBlur()}
      renderOption={(o: MicrosoftGroupDescription) => <>{o.name}</>}
      filterOptions={(os, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => t.exec(o.name));
      }}
    />
  );
};

export const ActionSettingsMicrosoftAddToGroup = ({
  expTpl,
  onChange,
  setValidation,
  validation,
  value,
  aTpl
}: {
  value: IAutomationActionTemplateMicrosoftAddToGroup;
  onChange: (nextValue: IAutomationActionTemplateMicrosoftAddToGroup) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();

  const [groups, loading, error] = useGroupList(org.id);

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_MICROSOFT_ADD_TO_GROUP,
    'pplToAdd'
  );

  const validateGroup = useFieldValidator(
    setValidation,
    VALIDATORS_MICROSOFT_ADD_TO_GROUP,
    'group'
  );

  const invalidPpltoAdd = validation?.fields.pplToAdd || null;
  const invalidGroup = validation?.fields.group || null;

  const groupsLoadErrorMessage = useMemo(() => {
    if (!error) {
      return null;
    }
    return t('Something went wrong loading groups. Please try again.');
  }, [t, error]);

  if (loading) {
    return <Loader />;
  }

  if (!groups) {
    return <SomethingWentWrong height={150} />;
  }

  return (
    <div>
      <FormRow>
        <ActionActorSelectorMulti
          error={!!invalidPpltoAdd}
          expTpl={expTpl}
          fullWidth
          helperText={invalidPpltoAdd}
          label={t('Who should be added')}
          value={value.pplToAdd.filter((p) => p.type === 'actor')}
          onBlur={() => validatePplToAdd(value, expTpl, aTpl)}
          filterOptions={(options) =>
            options.filter((a) => !value.pplToAdd.some((p) => isEqual(p, a)))
          }
          onChange={(nextPplToAdd) => {
            // artificially split to only contain actors, so we need to recompose
            const nextValue = {
              ...value,
              pplToAdd: nextPplToAdd.concat(
                value.pplToAdd.filter((p) => p.type === 'other')
              )
            };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
        />
      </FormRow>
      <FormRow>
        <GroupSelector
          groups={groups || EMPTY_ARR}
          error={!!(invalidGroup || groupsLoadErrorMessage)}
          helperText={invalidGroup || groupsLoadErrorMessage}
          value={value.group}
          onBlur={() => validateGroup(value, expTpl, aTpl)}
          onChange={(nextGroup) => {
            const nextValue = { ...value, group: nextGroup };
            invalidGroup && validateGroup(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
        />
      </FormRow>
    </div>
  );
};

export const ActionSettingsMicrosoftAddToGroupReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateMicrosoftAddToGroup;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be added')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel
          label={t('Which group should the member be added to')}
        >
          {value.group.name}
        </DataFieldWithLabel>
      </FormRow>
    </div>
  );
};
